import React, { useEffect, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { ThinPlusInCircle } from "assets/material-custom-icons";
import { DealRoleGuard, RoleComponentGuard } from "components/guards";
import { ActionRoleGuard } from "components/guards/Action-role.guard";
import { Task } from "components/Task-manage";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useNavigationHooks } from "hooks";
import { useDealSelector, useTaskManageActions, useTasksSelect } from "store";
import { TaskQueries, TaskStatus } from "store/types";

import { RoleName } from "../../constants";

const defaultQueries: TaskQueries = {
  status: [],
  priority: null,
  deal: [],
  assignee: [],
  due_date: [],
  isMyOpen: false,
  isMyRecurring: false,
  insideDeal: false
};

const colorsStatuses: Record<TaskStatus, string> = {
  open: "#5d7782",
  "in-progress": "#b2d6f8",
  done: "#b2eaad"
};

export const Tasks: React.FC = () => {
  const { id, title } = useDealSelector();
  const tasks = useTasksSelect();

  const { setCreateAction, getTasksAction, updateTaskStatusAction } = useTaskManageActions();
  const { setParams } = useNavigationHooks();

  const { loading, action } = useActionHook(getTasksAction, { defaultLoadingState: true });

  const sortedTasksByDueDate = useMemo(() => {
    return tasks.sort((currentTask, nextTask) => {
      if (!currentTask.due_date) return 1;
      if (!nextTask.due_date) return -1;
      return new Date(nextTask.due_date).getTime() - new Date(currentTask.due_date).getTime();
    });
  }, [tasks]);

  useEffect(() => {
    if (!id) return;
    action({ ...defaultQueries, deal: [{ title: title, id: id }] });
  }, [id]);

  return (
    <Stack
      sx={{
        gap: "8px",
        borderRadius: "8px",
        p: "16px",
        bgcolor: "white",
        boxShadow: "0 1px 2px 0 #1212171a",
        overflow: "hidden",
        flexGrow: 1
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}>Tasks</Typography>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
          <IconButton
            onClick={() => setCreateAction({ isOpen: true })}
            size="sm"
            sx={{
              bgcolor: "#071F35",
              ".MuiSvgIcon-root": { color: "white" },
              ":hover": { bgcolor: "#071F35", ".MuiSvgIcon-root": { color: "white" } }
            }}
          >
            <AddIcon />
          </IconButton>
        </RoleComponentGuard>
      </Stack>
      {/* <Stack sx={{ gap: "4px", maxHeight: 140, minHeight: 93, overflow: "auto" }}> */}
      <Stack sx={{ gap: "4px", overflow: "auto", flexGrow: 1 }}>
        {loading ? (
          <Skeleton variant="rounded" width="100%" height="100%" />
        ) : sortedTasksByDueDate.length ? (
          sortedTasksByDueDate.map((item) => (
            <Stack key={item.id} direction="row" sx={{ gap: "8px" }}>
              <ActionRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                <CheckCircleOutlineIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.status === "open") {
                      updateTaskStatusAction({ id: item.id, status: "in-progress" });
                    }
                    if (item.status === "in-progress") {
                      updateTaskStatusAction({
                        id: item.id,
                        status: "done"
                      });
                    }
                    if (item.status === "done") {
                      updateTaskStatusAction({
                        id: item.id,
                        status: "open"
                      });
                    }
                  }}
                  sx={{
                    width: "20px",
                    color: colorsStatuses[item.status],
                    cursor: "pointer"
                  }}
                />
              </ActionRoleGuard>
              <MaterialTooltip title={item.title}>
                <Typography
                  onClick={() => setParams({ search: { task: item.id } })}
                  sx={{
                    fontSize: "16px",
                    color: "#071F35",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer"
                  }}
                >
                  {item.title}
                </Typography>
              </MaterialTooltip>
            </Stack>
          ))
        ) : (
          <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Stack
              onClick={() => setCreateAction({ isOpen: true })}
              sx={{
                flexGrow: 1,
                p: "10px",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#F6F8FC",
                cursor: "pointer"
              }}
            >
              <ThinPlusInCircle sx={{ fontSize: "24px" }} />
              <Typography sx={{ fontSize: "15px", color: "#071F35", fontWeight: 400 }}>Add New Task</Typography>
            </Stack>
          </DealRoleGuard>
        )}
      </Stack>
      <Task
        refetchTable={() => {
          action({ ...defaultQueries, deal: [{ title: title, id: id }] });
        }}
      />
    </Stack>
  );
};
